import { useState } from "react";
import { motion } from "framer-motion";
import {
  X,
  Bell,
  Mountains,
  Leaf,
  Thermometer,
  Fire,
  Lightning as LightningIcon,
  Mountains as VolcanoIcon,
} from "@phosphor-icons/react";

const ALERTS_KEY = "conexus_alert_settings";

const DEFAULTS = {
  earthquake_mag: 5.0,
  ndvi_min: 0.2,
  temp_max: 42,
  temp_min: -25,
  alert_wildfires: true,
  alert_storms: true,
  alert_volcanoes: true,
};

export function loadAlertSettings() {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(ALERTS_KEY) || "{}") };
  } catch {
    return { ...DEFAULTS };
  }
}

export function persistAlertSettings(s) {
  localStorage.setItem(ALERTS_KEY, JSON.stringify(s));
}

export default function AlertSettings({ onClose, settings, onSave }) {
  const [local, setLocal] = useState({ ...DEFAULTS, ...settings });

  const update = (key, val) => setLocal((prev) => ({ ...prev, [key]: val }));

  const handleSave = () => {
    persistAlertSettings(local);
    onSave(local);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[2000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="alert-settings-overlay"
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        className="bg-stone-900 rounded-2xl border border-stone-700 w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="alert-settings-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-800">
          <div className="flex items-center gap-2">
            <Bell size={18} weight="bold" className="text-amber-400" />
            <span className="text-sm font-heading font-semibold text-white">Alert Thresholds</span>
          </div>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-white p-1 rounded-lg hover:bg-stone-800"
            data-testid="alert-settings-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Earthquake */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Mountains size={16} className="text-orange-400" />
              <span className="text-xs text-white font-body">Earthquake Magnitude</span>
              <span className="ml-auto text-xs font-mono text-emerald-400">
                M{local.earthquake_mag.toFixed(1)}+
              </span>
            </div>
            <input
              type="range"
              min={2}
              max={9}
              step={0.5}
              value={local.earthquake_mag}
              onChange={(e) => update("earthquake_mag", parseFloat(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none bg-stone-700 accent-orange-500"
              data-testid="alert-earthquake-slider"
            />
            <div className="flex justify-between text-[10px] text-stone-600 mt-1">
              <span>M2.0</span>
              <span>M9.0</span>
            </div>
          </div>

          {/* NDVI */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Leaf size={16} className="text-emerald-400" />
              <span className="text-xs text-white font-body">NDVI Minimum Alert</span>
              <span className="ml-auto text-xs font-mono text-emerald-400">{local.ndvi_min.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min={-0.5}
              max={1}
              step={0.05}
              value={local.ndvi_min}
              onChange={(e) => update("ndvi_min", parseFloat(e.target.value))}
              className="w-full h-1.5 rounded-full appearance-none bg-stone-700 accent-emerald-500"
              data-testid="alert-ndvi-slider"
            />
            <div className="flex justify-between text-[10px] text-stone-600 mt-1">
              <span>-0.5</span>
              <span>1.0</span>
            </div>
          </div>

          {/* Temperature */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Thermometer size={16} className="text-red-400" />
              <span className="text-xs text-white font-body">Temperature Extremes</span>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-[10px] text-stone-500 block mb-1">Min (cold alert)</label>
                <div className="flex items-center gap-2 bg-stone-800 rounded-lg px-3 py-1.5 border border-stone-700">
                  <input
                    type="number"
                    value={local.temp_min}
                    onChange={(e) => update("temp_min", parseFloat(e.target.value) || -25)}
                    className="bg-transparent text-white text-xs w-full focus:outline-none"
                    data-testid="alert-temp-min"
                  />
                  <span className="text-xs text-stone-500">°C</span>
                </div>
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-stone-500 block mb-1">Max (heat alert)</label>
                <div className="flex items-center gap-2 bg-stone-800 rounded-lg px-3 py-1.5 border border-stone-700">
                  <input
                    type="number"
                    value={local.temp_max}
                    onChange={(e) => update("temp_max", parseFloat(e.target.value) || 42)}
                    className="bg-transparent text-white text-xs w-full focus:outline-none"
                    data-testid="alert-temp-max"
                  />
                  <span className="text-xs text-stone-500">°C</span>
                </div>
              </div>
            </div>
          </div>

          {/* Event types */}
          <div>
            <span className="text-xs text-white font-body block mb-2">Event Type Alerts</span>
            <div className="flex flex-wrap gap-2">
              {[
                { key: "alert_wildfires", label: "Wildfires", Icon: Fire, color: "text-red-400" },
                { key: "alert_storms", label: "Storms", Icon: LightningIcon, color: "text-violet-400" },
                { key: "alert_volcanoes", label: "Volcanoes", Icon: VolcanoIcon, color: "text-orange-400" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => update(opt.key, !local[opt.key])}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-body border transition-colors ${
                    local[opt.key]
                      ? "bg-stone-800 border-stone-600 text-white"
                      : "bg-stone-800/50 border-stone-700/50 text-stone-600"
                  }`}
                  data-testid={`alert-toggle-${opt.key}`}
                >
                  <opt.Icon size={14} weight="bold" className={local[opt.key] ? opt.color : "text-stone-600"} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-stone-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="text-stone-400 text-xs px-4 py-2 rounded-lg hover:bg-stone-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-5 py-2 rounded-lg transition-colors"
            data-testid="alert-save-btn"
          >
            Save Settings
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
